// Disabling eslint for the next comment as we want to be able to use a disallowed word
// eslint-disable-next-line no-warning-comments
/**
 * The SDK creation factory. Create an instance of the SDK by calling this factory with the desired configurations.
 * The SDK instance will be referred as 'api' throughout the rest of the documentation content.
 * @public
 * @method create
 * @param {config} config The configuration object.
 * @return {api} The SDK instance.
 * @example
 * // Instantiate the SDK.
 * import { create } from 'kandy'
 * const client = create({
 *     authentication: { ... },
 *     logs: { ... },
 *     ...
 * });
 * // Use the SDK's API.
 * client.on( ... );
 */

/**
 * The 'api' is the type returned by the create function.
 * It contains various top-level functions that pertain to SDK global instance
 * as well as several nested namespaces that pertain to various features (e.g. call, contacts, presence, etc).
 *
 * @public
 * @module api
 */

/**
 * The configuration object. This object defines what different configuration
 * values you can use when instantiating the SDK using the {@link create} function.
 * @public
 * @module config
 */

// Disabling eslint for the next comment as we want to be able to use a disallowed word
// eslint-disable-next-line no-warning-comments
/**
 * A set of {@link call.SdpHandlerFunction SdpHandlerFunction}s for manipulating SDP information.
 * These handlers are used to customize low-level call behaviour for very specific
 * environments and/or scenarios.
 *
 * Note that SDP handlers are exposed on the entry point of the SDK. They can be added during
 * initialization of the SDK using the {@link #configconfigcall config.call.sdpHandlers} configuration
 * parameter. They can also be set after the SDK's creation by using the
 * {@link call.setSdpHandlers} function.
 *
 * @public
 * @namespace sdpHandlers
 * @example
 * import { create, sdpHandlers } from 'kandy';
 * const codecRemover = sdpHandlers.createCodecRemover(['VP8', 'VP9'])
 * const client = create({
 *   call: {
 *     sdpHandlers: [ codecRemover, <Your-SDP-Handler-Function>, ...]
 *   }
 * })
 * @example
 * // Through the Call API post-instantiation
 * client.call.setSdpHandlers([ codecRemover, <Your-SDP-Handler-Function>, ...])
 */

/**
 * An object that represents a selector to match codecs of an RTP map in SDP.
 *
 * @public
 * @static
 * @typedef {Object} CodecSelector
 * @memberof sdpHandlers
 * @property {string} name The name of the codec.
 * @property {Array<string>} fmtpParams An array of strings to match against the "a=fmtp" format parameters for the corresponding codec.
 *                                      All of the elements in the array must be contained in the "a=fmtp" attribute in order to be a match.
 */

/**
 * This function creates an SDP handler that will remove codecs matching the selectors specified for SDP offers and answers.
 *
 * In some scenarios it's necessary to remove certain codecs being offered by the SDK to remote parties. For example, some legacy call services limit the SDP
 * length (usually to 4KB) and will reject calls that have SDP size above this amount.
 *
 * While creating an SDP handler would allow a user to perform this type of manipulation, it is a non-trivial task that requires in-depth knowledge of WebRTC SDP.
 *
 * To facilitate this common task, the createCodecRemover function creates a codec removal handler that can be used for this purpose. Applications can use this codec
 * removal handler in combination with the {@link call.getAvailableCodecs call.getAvailableCodecs} function in order to build logic to determine the best codecs to use
 * for their application.
 *
 * @public
 * @memberof sdpHandlers
 * @method createCodecRemover
 * @param {Array<CodecSelector> | Array<string>} codecs A list of codec selectors to remove from the SDP. If passing a list of strings, they will be converted into
 *                                                      codec selectors that correspond to those names without any extra FMTP parameters.
 * @returns {call.SdpHandlerFunction} The resulting SDP handler that will remove the codec.
 * @example
 * import { create, sdpHandlers } from 'kandy';
 *
 * const codecRemover = sdpHandlers.createCodecRemover([
 *   // Remove all VP8 and VP9 codecs.
 *   'VP8',
 *   'VP9',
 *
 *   // Remove all H264 codecs with the specified FMTP parameters.
 *   {
 *     name: 'H264',
 *     fmtpParams: ['profile-level-id=4d0032', 'packetization-mode=1']
 *   }
 * ])
 *
 * const client = create({
 *   call: {
 *     sdpHandlers: [codecRemover]
 *   }
 * })
 *
 */

/*
 * Index template file that is used to create pre-defined version of the SDK.
 */
import { factory } from '../../packages/kandy/src/factory'
import { _ } from 'lodash/fp'

// This is a hack to fix an issue where Lodash will add itself to
// the window scope even if it's loaded via ES6. You can remove this when
// we have an answer to https://github.com/lodash/lodash/issues/1798 .
// eslint-disable-next-line
if (_) _.noConflict()

export default function commonIndex (options = {}, plugins = []) {
  return factory(plugins, options)
}
